import axios from 'axios';
import type { SessionInfo } from '../interfaces/SessionInfo';
import { AES, Base64, SHA256, Utf8 } from 'crypto-es';
import Cookies from 'js-cookie';
import { decodeJWT } from '../_features_/shared/utils';

const tokenEncryptionKey = import.meta.env.VITE_TOKEN_ENCRYPTION_KEY;

export class AuthenticationService {
	static generateCodeVerifier() {
		return AuthenticationService.generateRandomString(128);
	}

	static generateRandomString(length: number): string {
		let text = '';
		const possibleText = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz9876543210-._';
		const array = new Uint32Array(length);
		self.crypto.getRandomValues(array);

		for (const num of array) {
			text += possibleText.charAt(num % possibleText.length);
		}
		return text;
	}

	static generateCodeChallenge(code_verifier: string): string {
		sessionStorage.setItem('code_verifier', code_verifier);
		const hashedCodeVerifier = SHA256(code_verifier).toString(Base64);
		return AuthenticationService.base64URL(hashedCodeVerifier);
	}

	static base64URL(code: string): string {
		return code.replaceAll('=', '').replaceAll('+', '-').replaceAll('/', '_');
	}

	// Encrypt a token
	private encryptToken(token: string): string {
		return AES.encrypt(token, tokenEncryptionKey).toString();
	}

	// Decrypt a token
	private decryptToken(encryptedToken: string): string | null {
		const bytes = AES.decrypt(encryptedToken, tokenEncryptionKey);
		return bytes.toString(Utf8);
	}

	public getAccessToken(): string | null {
		const encryptedToken = Cookies.get('access_token');
		return encryptedToken ? this.decryptToken(encryptedToken) : null;
	}

	public getRefreshToken(): string | null {
		const encryptedToken = Cookies.get('refresh_token');
		return encryptedToken ? this.decryptToken(encryptedToken) : null;
	}

	public async updateTokens(): Promise<SessionInfo | undefined> {
		try {
			const refreshToken = this.getRefreshToken();
			if (refreshToken) {
				const response = await axios.post(
					import.meta.env.VITE_AUTH_TOKEN_URI,
					new URLSearchParams({
						grant_type: 'refresh_token',
						refresh_token: refreshToken
					}),
					{
						headers: {
							Accept: 'application/json, text/plain, */*',
							'Content-Type': 'application/x-www-form-urlencoded'
						}
					}
				);
				const sessionInfos = response.data;

				if (!sessionInfos.access_token) {
					sessionStorage.clear();
				}

				for (const key of Object.keys(sessionInfos)) {
					try {
						if (key === 'expires_in') {
							Cookies.set(key, sessionInfos[key]);
						} else if (key === 'token_type') {
							Cookies.set(key, 'Bearer');
						} else if (key === 'id_token') {
							const decodedIdToken = decodeJWT(sessionInfos[key]);
							localStorage.setItem('userInfo', JSON.stringify(decodedIdToken.payload));
						} else {
							const encryptedValue = this.encryptToken(sessionInfos[key]);
							Cookies.set(key, encryptedValue);
						}
					} catch (error) {
						console.error(`Failed to process session key ${key}:`, error);
						sessionStorage.clear();
						this.clearCookies();
						this.connectIfNoAccessToken();
						break;
					}
				}
				return sessionInfos;
			}
			sessionStorage.clear();
			this.clearCookies();
			await this.connectIfNoAccessToken();
		} catch (error) {
			sessionStorage.clear();
			this.clearCookies();
			await this.connectIfNoAccessToken();
		}
	}

	public clearCookies() {
		Cookies.remove('access_token');
		Cookies.remove('refresh_token');
		Cookies.remove('token_type');
		Cookies.remove('expires_in');
	}

	public async connectIfNoAccessToken() {
		if (!sessionStorage.getItem('code_verifier') && !Cookies.get('access_token')) {
			const code_verifier = AuthenticationService.generateCodeVerifier();
			const code_challenger = AuthenticationService.generateCodeChallenge(code_verifier);
			const authCodeFlowConfig = new URLSearchParams({
				client_id: import.meta.env.VITE_AUTH_CLIENT_ID,
				redirect_uri: import.meta.env.VITE_AUTH_REDIRECT_URI,
				response_type: 'code',
				scope: 'openid profile',
				code_challenge_method: 'S256',
				code_challenge: code_challenger
			});
			window.location.replace(
				import.meta.env.VITE_AUTH_AUTHORIZATION_URI + '?' + authCodeFlowConfig
			);
		}
		if (sessionStorage.getItem('code_verifier') && !Cookies.get('access_token')) {
			const urlParam = new URLSearchParams(window.location.search);
			const authorization_code = urlParam.get('code');
			if (!authorization_code) return;

			const result = await axios.post(
				import.meta.env.VITE_AUTH_TOKEN_URI,
				new URLSearchParams({
					grant_type: 'authorization_code',
					client_id: import.meta.env.VITE_AUTH_CLIENT_ID,
					code: authorization_code,
					redirect_uri: import.meta.env.VITE_AUTH_REDIRECT_URI,
					code_verifier: sessionStorage.getItem('code_verifier') as string
				}),
				{
					headers: {
						Accept: 'application/json, text/plain, */*',
						'Content-Type': 'application/x-www-form-urlencoded'
					}
				}
			);
			const sessionInfos = result.data;

			if (!sessionInfos?.access_token) return;
			for (const key of Object.keys(sessionInfos)) {
				try {
					if (key === 'expires_in') {
						Cookies.set(key, sessionInfos[key]);
					} else if (key === 'token_type') {
						Cookies.set(key, 'Bearer');
					} else if (key === 'id_token') {
						const decodedIdToken = decodeJWT(sessionInfos[key]);
						localStorage.setItem('userInfo', JSON.stringify(decodedIdToken.payload));
					} else {
						const encryptedValue = this.encryptToken(sessionInfos[key]);
						Cookies.set(key, encryptedValue);
					}
				} catch (error) {
					console.error(`Failed to process session key ${key}:`, error);
					sessionStorage.clear();
					this.clearCookies();
					this.connectIfNoAccessToken();
					break;
				}
			}
			window.location.replace(import.meta.env.VITE_AUTH_REDIRECT_URI);
		}
	}
}
